import { useState, useEffect } from 'react';
import { informeService } from '@/services/informeService';
import { InformeResponse, ApiResponse } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Hook para gestionar informes por partido y entrenador autenticado
 * @param partidoId - ID del partido para obtener sus informes
 */
export const useInformes = (partidoId?: number) => {
  const { user } = useAuth();
  const [informes, setInformes] = useState<InformeResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Obtiene los informes de un partido específico filtrados por entrenador autenticado
   */
  const obtenerInformes = async (id: number) => {
    if (!user?.id) {
      setError('Usuario no autenticado');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response: ApiResponse<InformeResponse[]> = await informeService.getInformesByPartidoYEntrenador(id, user.id);
      
      if (response.success && response.data) {
        setInformes(response.data);
      } else {
        setError(response.message || 'Error al obtener informes');
      }
    } catch (err) {
      setError('Error de conexión al obtener informes');
      console.error('Error al obtener informes:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Actualiza un informe existente
   * @param id - ID del informe a actualizar
   * @param data - Datos del informe a actualizar
   * @returns Promise con el informe actualizado
   */
  const actualizarInforme = async (id: number, data: Partial<InformeResponse>): Promise<InformeResponse> => {
    try {
      setLoading(true);
      setError(null);
      
      // Siempre crear FormData para manejar tanto datos como archivos
      const formData = new FormData();
      
      // Agregar todos los campos excepto foto
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'foto') {
          formData.append(key, String(value));
        }
      });
      
      // Manejar la foto: si hay nueva foto, agregarla; si no, mantener la existente
      if (data.foto && data.foto instanceof File) {
        // Nueva foto seleccionada
        formData.append('foto', data.foto);
      }
      // Si no hay nueva foto pero el informe tiene foto, no agregamos nada al FormData
      // para que el backend mantenga la foto existente
      
      const response = await informeService.actualizarInforme(id, formData);
      
      // Verificar si la respuesta es directamente InformeResponse o ApiResponse<InformeResponse>
      let informeActualizado: InformeResponse;
      
      // Caso 1: El backend devuelve directamente InformeResponse (como muestran los logs)
      if (response && typeof response === 'object' && 'nombreCompleto' in response && !('data' in response)) {
        informeActualizado = response as InformeResponse;
      }
      // Caso 2: El backend devuelve ApiResponse<InformeResponse>
      else if (response && typeof response === 'object' && 'data' in response && response.data) {
        const apiResponse = response as ApiResponse<InformeResponse>;
        if (apiResponse.data && typeof apiResponse.data === 'object' && 'id' in apiResponse.data) {
          informeActualizado = apiResponse.data;
        } else {
          const errorMessage = apiResponse.message || 'Error al actualizar informe';
          setError(errorMessage);
          throw new Error(errorMessage);
        }
      }
      // Caso 3: Error - no hay datos válidos
      else {
        const errorMessage = (response as any)?.message || 'Error al actualizar informe';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Actualizar el informe en la lista local
      setInformes(prev => 
        prev.map(informe => 
          informe.id === id ? informeActualizado : informe
        )
      );
      
      return informeActualizado;
      
    } catch (err) {
      setError('Error de conexión al actualizar informe');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresca la lista de informes
   */
  const refrescarInformes = () => {
    if (partidoId) {
      obtenerInformes(partidoId);
    }
  };

  // Cargar informes automáticamente cuando se proporciona partidoId y hay usuario autenticado
  useEffect(() => {
    if (partidoId && user?.id) {
      obtenerInformes(partidoId);
    }
  }, [partidoId, user?.id]);

  return {
    informes,
    loading,
    error,
    obtenerInformes,
    actualizarInforme,
    refrescarInformes
  };
};